import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A value that follows something slower than the user's hand — here the URL. While a slider is being
 * dragged the live value is held locally (so the picture keeps up with the hand), and it is written
 * back — `commit` — after the hand has been still for `delay` ms, or at once when the change is final
 * (pointer released, key pressed). As soon as `committed` moves, the local draft is dropped and the URL
 * is the truth again, so Back / Forward and edits made elsewhere are never overruled by a stale draft.
 */
export function useCommittedValue<T>(
  committed: T,
  onCommit: (value: T) => void,
  delay = 350,
): [value: T, set: (value: T, immediate?: boolean) => void] {
  const [draft, setDraft] = useState<{ value: T; base: T } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const commit = useRef(onCommit);

  useEffect(() => {
    commit.current = onCommit;
  }, [onCommit]);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  // The URL moved on from the value the draft was made against: drop the draft ("adjusting state
  // while rendering" — the supported way to derive state from a changed input).
  if (draft && !Object.is(draft.base, committed)) setDraft(null);

  const set = useCallback(
    (value: T, immediate = false) => {
      setDraft((previous) => ({ value, base: previous ? previous.base : committed }));
      window.clearTimeout(timer.current);
      if (immediate) {
        commit.current(value);
      } else {
        timer.current = window.setTimeout(() => {
          commit.current(value);
        }, delay);
      }
    },
    [committed, delay],
  );

  return [draft ? draft.value : committed, set];
}
